import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Controller, EffectCoverflow } from 'swiper';
import { FC, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { BannerableType } from '@/logic/services';
import { HomeSliderProps } from './home-slider';
import PostersSliderItem from './posters-slider-item';
import SwiperInterface from 'swiper';
import { getDirection } from '@/lang/utils';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useRouter } from 'next/router';

// TODO: check if you want to use local from useRouter instead of them.direction

interface PostersSliderProps extends HomeSliderProps {
  swiper: SwiperInterface[];
  handleSwiper: (swiper: SwiperInterface) => void;
}

export const PostersSlider: FC<PostersSliderProps> = ({
  banners,
  swiper,
  handleSwiper,
}) => {
  const [activeBannerRealIndex, setActiveBannerRealIndex] = useState(0);
  const [activeBannerSnapIndex, setActiveBannerSnapIndex] = useState(banners.length);

  const theme = useTheme();
  const router = useRouter();
  const { cityId } = useCity();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('laptop'));

  const { locale } = useRouter();
  const dir = getDirection(locale);

  return (
    <Box
      sx={{
        marginTop: {
          mobile: '-90px',
          laptop: pxToRem(-400),
        },
        // Before Swiper initializes there is no flex row and no per-slide
        // width, so every slide stacks vertically and the container renders
        // ~808px tall on mobile. Coverflow lays them into a single row on
        // init and it collapses to ~322px, dragging everything below it up
        // by ~486px — measured as a single 0.231 layout shift at 412px wide.
        //
        // Reserving the settled height up front holds the box steady across
        // that transition. The settled geometry is exact: posters are 2:3
        // (1600x2400) and each slide is width/slidesPerView wide, so the
        // content box is
        //   height = width / slidesPerView * 1.5
        //   mobile  width/3 * 1.5 = width * 0.5  -> aspect-ratio 2/1
        //   laptop  width/5 * 1.5 = width * 0.3  -> aspect-ratio 10/3
        // aspect-ratio resolves against the content box, which is what the
        // slides size against too — a `vw` formula would over-reserve by the
        // scrollbar width (measured 485px content vs 500px viewport).
        //
        // box-sizing: content-box keeps paddingBottom additive on top of the
        // ratio rather than eating into it. Note the padding below is keyed
        // `mobile`, which in MUI means "from 0px up" — it applies at every
        // width, laptop included. Verified against the rendered box:
        //   412px viewport  242.5 + 80 = 322.5px
        //   1440px viewport 427.5 + 80 = 507.5px
        '.swiper': {
          boxSizing: 'content-box',
          aspectRatio: {
            mobile: '2 / 1',
            laptop: '10 / 3',
          },
          paddingBottom: {
            mobile: '80px',
          },
        },
        '.swiper-slide': {
          transformOrigin: {
            laptop: 'center bottom',
          },
        },
        '.swiper-slide-active': {
          filter:
            'drop-shadow(4px 0px 35px rgba(0, 0, 0, 0.25)) drop-shadow(-4px 0px 35px rgba(0, 0, 0, 0.25))', //TODO: Move to palette file
        },
      }}
    >
      <Swiper
        onSwiper={handleSwiper}
        speed={600}
        dir={dir}
        loop
        slidesPerView={isMediumScreen ? 5 : 3}
        loopedSlides={banners.length * 2}
        centeredSlides
        slideToClickedSlide
        modules={[EffectCoverflow, Controller]}
        controller={{
          control: swiper,
        }}
        effect='coverflow'
        coverflowEffect={{
          depth: 0,
          rotate: 0,
          scale: 0.8,
          slideShadows: false,
        }}
        onTransitionEnd={swiper => {
          setActiveBannerRealIndex(swiper.realIndex);
          setActiveBannerSnapIndex(swiper.snapIndex);
        }}
        onClick={swiper => {
          if (swiper.snapIndex === activeBannerSnapIndex) {
            const activeBanner = banners[activeBannerRealIndex];
            activeBanner.bannerableType === BannerableType.FILM
              ? router.push(`/movies/${activeBanner.film.uniqueName}?cityId=${cityId}`)
              : activeBanner.bannerableType === BannerableType.OFFER
              ? router.push(`/offers/${activeBanner.offer.uniqueName}?cityId=${cityId}`)
              : activeBanner.bannerableType === BannerableType.EXTERNAL_LINK
              ? window.open(activeBanner.url)
              : null;
          }
        }}
      >
        {banners.map((banner, index) => (
          <SwiperSlide key={banner.id}>
            <PostersSliderItem
              title={
                banner.bannerableType === BannerableType.FILM
                  ? banner.film.title
                  : banner.bannerableType === BannerableType.OFFER
                  ? banner.offer.title
                  : banner.buttonText
              }
              img={banner.image}
              bannerPosterType={banner.bannerableType}
              // The centred first poster is the LCP element on mobile. Only it
              // is eager — the rest stay lazy so we do not trade one slow image
              // for a burst of them.
              priority={index === 0}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};
